/**
 * Procurement emails: RFQ / request updates / purchase orders to suppliers.
 * Uses email-server via sendEmail (same as rest of the app).
 */
import { sendEmail, getPortalUrlForEmails } from './emailService';

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatLineItemsText(items) {
  if (!Array.isArray(items) || !items.length) return '—';
  return items
    .filter((row) => (row.item || '').trim() || (Number(row.quantity) || 0) > 0)
    .map(
      (row, i) =>
        `${i + 1}. ${(row.item || '').trim() || 'Item'} — Qty ${row.quantity ?? '—'}${row.specification ? ` — ${row.specification}` : ''}`,
    )
    .join('\n');
}

function formatLineItemsHtml(items) {
  const rows = (items || [])
    .filter((row) => (row.item || '').trim() || (Number(row.quantity) || 0) > 0)
    .map(
      (row, i) =>
        `<tr><td style="padding:8px 10px;border-bottom:1px solid #e2e8f0">${i + 1}</td><td style="padding:8px 10px;border-bottom:1px solid #e2e8f0">${escapeHtml((row.item || '').trim() || '—')}</td><td style="padding:8px 10px;border-bottom:1px solid #e2e8f0;text-align:center">${escapeHtml(String(row.quantity ?? '—'))}</td><td style="padding:8px 10px;border-bottom:1px solid #e2e8f0">${escapeHtml((row.specification || '').trim() || '—')}</td></tr>`,
    );
  if (!rows.length) return '<p>—</p>';
  return `<table style="border-collapse:separate;border-spacing:0;width:100%;font-size:13px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden"><thead><tr style="background:#f8fafc"><th style="padding:8px 10px;border-bottom:1px solid #e2e8f0;text-align:left;width:42px">#</th><th style="padding:8px 10px;border-bottom:1px solid #e2e8f0;text-align:left">Required item</th><th style="padding:8px 10px;border-bottom:1px solid #e2e8f0;text-align:center;width:80px">Qty</th><th style="padding:8px 10px;border-bottom:1px solid #e2e8f0;text-align:left">Specification</th></tr></thead><tbody>${rows.join('')}</tbody></table>`;
}

function formatPoLinesText(rows) {
  if (!Array.isArray(rows) || !rows.length) return '—';
  return rows
    .filter((row) => (row.description || '').trim() && (Number(row.qty) || 0) > 0)
    .map(
      (row, i) =>
        `${i + 1}. ${(row.description || '').trim()} — ${row.qty ?? '—'} ${row.unit || ''} @ ${row.unitPrice ?? '—'} OMR`,
    )
    .join('\n');
}

function formatPoLinesHtml(rows) {
  const tr = (rows || [])
    .filter((row) => (row.description || '').trim() && (Number(row.qty) || 0) > 0)
    .map(
      (row, i) =>
        `<tr><td style="padding:6px 8px;border-bottom:1px solid #e2e8f0">${i + 1}</td><td style="padding:6px 8px;border-bottom:1px solid #e2e8f0">${escapeHtml((row.description || '').trim())}</td><td style="padding:6px 8px;border-bottom:1px solid #e2e8f0">${escapeHtml(String(row.qty))}</td><td style="padding:6px 8px;border-bottom:1px solid #e2e8f0">${escapeHtml((row.unit || '').trim() || '—')}</td><td style="padding:6px 8px;border-bottom:1px solid #e2e8f0">${escapeHtml(String(row.unitPrice ?? '—'))}</td></tr>`,
    );
  if (!tr.length) return '<p>—</p>';
  return `<table style="border-collapse:collapse;width:100%;font-size:13px"><thead><tr><th>#</th><th align="left">Description</th><th>Qty</th><th>Unit</th><th>Unit price (OMR)</th></tr></thead><tbody>${tr.join('')}</tbody></table>`;
}

/**
 * RFQ or “request updated” to invited suppliers.
 * @returns {Promise<{ sent: number; skipped: number; errors: string[] }>}
 */
export async function sendProcurementSupplierEmailsFromClient(opts) {
  const {
    type,
    recipientSupplierIds,
    suppliersList,
    requestNo,
    title,
    departmentProject,
    requestedBy,
    neededDate,
    lineItems,
    notes,
    priority,
    attachment,
  } = opts;

  const idSet = new Set(recipientSupplierIds || []);
  const portal = getPortalUrlForEmails();
  const errors = [];
  let sent = 0;
  let skipped = 0;

  const subjectBase =
    type === 'update'
      ? `Procurement request updated — ${requestNo || title || 'Request'}`
      : `Request for quotation — ${requestNo || title || 'RFQ'}`;

  const linesText = formatLineItemsText(lineItems);
  const linesHtml = formatLineItemsHtml(lineItems);
  const notesBlock = (notes || '').trim()
    ? `\nNotes:\n${notes.trim()}\n`
    : '';
  const notesHtml = (notes || '').trim()
    ? `<p><strong>Notes</strong></p><p style="color:#334155">${escapeHtml(notes.trim()).replace(/\n/g, '<br/>')}</p>`
    : '';
  const pri = (priority || '').trim();

  for (const s of suppliersList || []) {
    if (!s?.id || !idSet.has(s.id)) continue;
    const email = (s.primary_email || s.email || '').trim();
    const company = s.company_name || s.companyName || 'Supplier';
    const contactName = Array.isArray(s.contacts)
      ? (s.contacts.find((c) => c?.is_primary)?.name || s.contacts.find((c) => c?.name)?.name || '').trim()
      : '';
    const recipientName = contactName || company;
    if (!email) {
      skipped += 1;
      errors.push(`${company}: no primary email`);
      continue;
    }

    const intro =
      type === 'update'
        ? `Please be advised that the following procurement request has been updated. Kindly review the revised details below.`
        : `You are invited to submit your formal quotation for the procurement request detailed below.`;

    const body = `Dear ${recipientName},

${intro}

Request: ${requestNo || '—'}
Title: ${title || '—'}
Priority: ${pri || '—'}
Department / project: ${departmentProject || '—'}
Requested by: ${requestedBy || '—'}
Needed by: ${neededDate || '—'}
${notesBlock}
Line items:
${linesText}

Please submit your quotation by replying to this email, and include your quoted prices, delivery lead time, and payment terms.

Portal: ${portal}/procurement

— ECDT Portal`;

    const html = `<div style="max-width:680px;margin:0 auto;font-family:Inter,Segoe UI,Arial,sans-serif;font-size:14px;color:#0f172a;line-height:1.6">
      <div style="background:#0f172a;color:#ffffff;padding:14px 18px;border-radius:10px 10px 0 0">
        <div style="font-size:16px;font-weight:600">ECDT Procurement</div>
        <div style="font-size:12px;opacity:.88">Request for Quotation</div>
      </div>
      <div style="border:1px solid #e2e8f0;border-top:0;border-radius:0 0 10px 10px;padding:18px">
        <p style="margin:0 0 10px">Dear <strong>${escapeHtml(recipientName)}</strong>,</p>
        <p style="margin:0 0 14px">${escapeHtml(intro)}</p>
        <table style="width:100%;border-collapse:separate;border-spacing:0;font-size:13px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin:0 0 14px">
          <tbody>
            <tr><td style="padding:8px 10px;background:#f8fafc;color:#475569;width:180px">Request No.</td><td style="padding:8px 10px"><strong>${escapeHtml(requestNo || '—')}</strong></td></tr>
            <tr><td style="padding:8px 10px;background:#f8fafc;color:#475569">Title</td><td style="padding:8px 10px">${escapeHtml(title || '—')}</td></tr>
            <tr><td style="padding:8px 10px;background:#f8fafc;color:#475569">Priority</td><td style="padding:8px 10px">${escapeHtml(pri || '—')}</td></tr>
            <tr><td style="padding:8px 10px;background:#f8fafc;color:#475569">Department / Project</td><td style="padding:8px 10px">${escapeHtml(departmentProject || '—')}</td></tr>
            <tr><td style="padding:8px 10px;background:#f8fafc;color:#475569">Requested by</td><td style="padding:8px 10px">${escapeHtml(requestedBy || '—')}</td></tr>
            <tr><td style="padding:8px 10px;background:#f8fafc;color:#475569">Required date</td><td style="padding:8px 10px">${escapeHtml(neededDate || '—')}</td></tr>
          </tbody>
        </table>
        ${notesHtml}
        <p style="margin:10px 0 8px"><strong>Required items</strong></p>
        ${linesHtml}
        <p style="margin-top:14px">Kindly submit your formal quotation by replying to this email and include your offered prices, delivery lead time, and payment terms.</p>
        <p style="margin-top:14px"><a href="${escapeHtml(portal)}/procurement">Open procurement in ECDT Portal</a></p>
        <p style="margin-top:16px;color:#64748b;font-size:12px">Regards,<br/>ECDT Procurement Team</p>
      </div>
    </div>`;

    const result = await sendEmail({
      to: email,
      subject: subjectBase,
      body,
      html,
      attachments: attachment ? [attachment] : undefined,
    });
    if (result.success) sent += 1;
    else errors.push(`${company}: ${result.error || 'send failed'}`);
  }

  return { sent, skipped, errors };
}

/**
 * Purchase order issued — email to the selected supplier.
 * @returns {Promise<{ success: boolean; error?: string }>}
 */
export async function sendPurchaseOrderEmailToSupplier(opts) {
  const {
    supplierRecord,
    orderNo,
    orderDate,
    deliveryDate,
    paymentMode,
    paymentTerms,
    notes,
    lineItems,
    totalAmount,
    orderedBy,
    approvedBy,
    poRef,
    refQuotation,
    attachment,
  } = opts;

  const email = (supplierRecord?.primary_email || supplierRecord?.email || '').trim();
  if (!email) {
    return { success: false, error: 'Supplier has no primary email' };
  }
  const company = supplierRecord.company_name || supplierRecord.companyName || 'Supplier';
  const contactName = Array.isArray(supplierRecord.contacts)
    ? (supplierRecord.contacts.find((c) => c?.is_primary)?.name || supplierRecord.contacts.find((c) => c?.name)?.name || '').trim()
    : '';
  const recipientName = contactName || company;
  const portal = getPortalUrlForEmails();
  const subject = `Purchase order ${orderNo || '—'}`;
  const linesText = formatPoLinesText(lineItems);
  const linesHtml = formatPoLinesHtml(lineItems);

  const body = `Dear ${recipientName},

Please find below a new purchase order from ECDT Portal.

PO number: ${orderNo || '—'}
PO ref: ${poRef || '—'}
Reference quotation: ${refQuotation || '—'}
Order date: ${orderDate || '—'}
Delivery date: ${deliveryDate || '—'}
Payment: ${paymentMode || '—'}
Terms: ${paymentTerms || '—'}
Ordered by: ${orderedBy || '—'}
Approved by: ${approvedBy || '—'}
${notes ? `Notes:\n${notes}\n` : ''}
Line items:
${linesText}

Estimated total: ${totalAmount != null ? `${totalAmount} OMR` : '—'}

Portal: ${portal}/procurement

— ECDT Portal`;

  const html = `<div style="max-width:640px;font-family:system-ui,sans-serif;font-size:14px;color:#0f172a">
    <p>Dear <strong>${escapeHtml(recipientName)}</strong>,</p>
    <p>Please find below a new <strong>purchase order</strong> from ECDT Portal.</p>
    <table style="border-collapse:collapse;margin:12px 0;font-size:14px">
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">PO number</td><td><strong>${escapeHtml(orderNo || '—')}</strong></td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">PO ref</td><td>${escapeHtml(poRef || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Ref. quotation</td><td>${escapeHtml(refQuotation || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Order date</td><td>${escapeHtml(orderDate || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Delivery date</td><td>${escapeHtml(deliveryDate || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Payment</td><td>${escapeHtml(paymentMode || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Terms</td><td>${escapeHtml(paymentTerms || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Ordered by</td><td>${escapeHtml(orderedBy || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Approved by</td><td>${escapeHtml(approvedBy || '—')}</td></tr>
    </table>
    ${notes ? `<p><strong>Notes</strong></p><p>${escapeHtml(notes).replace(/\n/g, '<br/>')}</p>` : ''}
    <p><strong>Line items</strong></p>
    ${linesHtml}
    <p><strong>Estimated total:</strong> ${escapeHtml(totalAmount != null ? `${totalAmount} OMR` : '—')}</p>
    <p style="margin-top:20px"><a href="${escapeHtml(portal)}/procurement">ECDT Portal — Procurement</a></p>
    <p style="margin-top:16px;color:#94a3b8;font-size:12px">— ECDT Portal</p>
  </div>`;

  return sendEmail({
    to: email,
    subject,
    body,
    html,
    attachments: attachment ? [attachment] : undefined,
  });
}

function getProcurementNotifyEmail() {
  return String(process.env.REACT_APP_PROCUREMENT_NOTIFY_EMAIL || '').trim();
}

/**
 * Internal notification when a procurement request is created.
 * Sends only if REACT_APP_PROCUREMENT_NOTIFY_EMAIL is configured.
 */
export async function sendProcurementRequestCreatedEmail(opts) {
  const to = getProcurementNotifyEmail();
  if (!to) return { success: true, skipped: true };

  const {
    requestNo,
    title,
    departmentProject,
    requestedBy,
    neededDate,
    priority,
    status,
    invitedCount,
  } = opts || {};
  const portal = getPortalUrlForEmails();
  const subject = `New procurement request — ${requestNo || title || 'Request'}`;
  const body = `A procurement request was created.

Request: ${requestNo || '—'}
Title: ${title || '—'}
Department / project: ${departmentProject || '—'}
Requested by: ${requestedBy || '—'}
Needed date: ${neededDate || '—'}
Priority: ${priority || '—'}
Status: ${status || '—'}
Suppliers invited: ${invitedCount ?? 0}

Open: ${portal}/procurement

— ECDT Portal`;
  const html = `<div style="max-width:640px;font-family:system-ui,sans-serif;font-size:14px;color:#0f172a">
    <p><strong>New procurement request created</strong></p>
    <table style="border-collapse:collapse">
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Request</td><td><strong>${escapeHtml(requestNo || '—')}</strong></td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Title</td><td>${escapeHtml(title || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Department / project</td><td>${escapeHtml(departmentProject || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Requested by</td><td>${escapeHtml(requestedBy || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Needed date</td><td>${escapeHtml(neededDate || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Priority</td><td>${escapeHtml(priority || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Status</td><td>${escapeHtml(status || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Suppliers invited</td><td>${escapeHtml(String(invitedCount ?? 0))}</td></tr>
    </table>
    <p style="margin-top:14px"><a href="${escapeHtml(portal)}/procurement">Open procurement</a></p>
    <p style="margin-top:14px;color:#94a3b8;font-size:12px">— ECDT Portal</p>
  </div>`;
  return sendEmail({ to, subject, body, html });
}

/**
 * Internal notification when a purchase order is created.
 * Sends only if REACT_APP_PROCUREMENT_NOTIFY_EMAIL is configured.
 */
export async function sendPurchaseOrderCreatedEmail(opts) {
  const to = getProcurementNotifyEmail();
  if (!to) return { success: true, skipped: true };

  const {
    orderNo,
    supplierName,
    orderDate,
    deliveryDate,
    totalAmount,
    paymentMode,
    paymentTerms,
    orderedBy,
    approvedBy,
  } = opts || {};
  const portal = getPortalUrlForEmails();
  const subject = `New purchase order — ${orderNo || 'PO'}`;
  const body = `A purchase order was created.

PO number: ${orderNo || '—'}
Supplier: ${supplierName || '—'}
Order date: ${orderDate || '—'}
Delivery date: ${deliveryDate || '—'}
Total: ${totalAmount != null ? `${totalAmount} OMR` : '—'}
Payment mode: ${paymentMode || '—'}
Payment terms: ${paymentTerms || '—'}
Ordered by: ${orderedBy || '—'}
Approved by: ${approvedBy || '—'}

Open: ${portal}/procurement

— ECDT Portal`;
  const html = `<div style="max-width:640px;font-family:system-ui,sans-serif;font-size:14px;color:#0f172a">
    <p><strong>New purchase order created</strong></p>
    <table style="border-collapse:collapse">
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">PO number</td><td><strong>${escapeHtml(orderNo || '—')}</strong></td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Supplier</td><td>${escapeHtml(supplierName || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Order date</td><td>${escapeHtml(orderDate || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Delivery date</td><td>${escapeHtml(deliveryDate || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Total</td><td>${escapeHtml(totalAmount != null ? `${totalAmount} OMR` : '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Payment mode</td><td>${escapeHtml(paymentMode || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Payment terms</td><td>${escapeHtml(paymentTerms || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Ordered by</td><td>${escapeHtml(orderedBy || '—')}</td></tr>
      <tr><td style="padding:4px 12px 4px 0;color:#64748b">Approved by</td><td>${escapeHtml(approvedBy || '—')}</td></tr>
    </table>
    <p style="margin-top:14px"><a href="${escapeHtml(portal)}/procurement">Open procurement</a></p>
    <p style="margin-top:14px;color:#94a3b8;font-size:12px">— ECDT Portal</p>
  </div>`;
  return sendEmail({ to, subject, body, html });
}

/** Optional: also call Supabase Edge Function (set REACT_APP_PROCUREMENT_USE_EDGE_EMAIL=true). */
export function shouldUseProcurementEdgeEmail() {
  return String(process.env.REACT_APP_PROCUREMENT_USE_EDGE_EMAIL || '').toLowerCase() === 'true';
}
